import React, { useRef, useState } from "react";
import styles from "./cambiarContraseñaPage.module.css";
import { useAuth } from "../../../../../context/authContext";
import {
  userService,
  formatApiError,
  PIN_LENGTH,
  pinDigits,
  emptyPinArray,
  getStoredPinLength,
  rememberPinLength,
  validateNewPin,
} from "../../../../../services/api";
import { PinInput, PinLengthToggle } from "../../../../../components/ui";

const CambiarContrasena = () => {
  const { user } = useAuth();
  // PIN actual: su propia longitud (la última usada en este dispositivo)
  const [currentLength, setCurrentLength] = useState(getStoredPinLength);
  // Nuevo PIN + confirmación comparten longitud (6 por defecto: más seguro)
  const [newLength, setNewLength] = useState(PIN_LENGTH);
  const [currentPin, setCurrentPin] = useState(() =>
    emptyPinArray(getStoredPinLength())
  );
  const [newPin, setNewPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const [confirmPin, setConfirmPin] = useState(() => emptyPinArray(PIN_LENGTH));
  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const currentPinRef = useRef(null);
  const newPinRef = useRef(null);

  const handleCurrentLengthChange = (length) => {
    setCurrentLength(length);
    setCurrentPin(emptyPinArray(length));
    setMessage({ type: "", text: "" });
    currentPinRef.current?.focus();
  };

  const handleNewLengthChange = (length) => {
    setNewLength(length);
    setNewPin(emptyPinArray(length));
    setConfirmPin(emptyPinArray(length));
    setMessage({ type: "", text: "" });
    newPinRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    const current = pinDigits(currentPin);
    const newP = pinDigits(newPin);
    const confirm = pinDigits(confirmPin);

    if (current.length !== currentLength) {
      setMessage({
        type: "error",
        text: `Ingresa tu PIN actual de ${currentLength} dígitos`,
      });
      return;
    }

    const newPinError = validateNewPin(newP, newLength);
    if (newPinError) {
      setMessage({ type: "error", text: newPinError });
      return;
    }

    if (confirm.length !== newLength) {
      setMessage({
        type: "error",
        text: `Confirma el nuevo PIN de ${newLength} dígitos`,
      });
      return;
    }

    if (newP !== confirm) {
      setMessage({ type: "error", text: "Los PINs nuevos no coinciden" });
      return;
    }

    if (newP === current) {
      setMessage({
        type: "error",
        text: "El nuevo PIN debe ser diferente al actual",
      });
      return;
    }

    if (!user?.id) {
      setMessage({ type: "error", text: "No hay sesión activa" });
      return;
    }

    setLoading(true);

    try {
      const result = await userService.changePassword(user.id, current, newP);
      if (result.ok) {
        rememberPinLength(newLength);
        setMessage({ type: "success", text: "PIN actualizado correctamente" });
        // El PIN actual pasa a ser el nuevo: su longitud también
        setCurrentLength(newLength);
        setCurrentPin(emptyPinArray(newLength));
        setNewPin(emptyPinArray(newLength));
        setConfirmPin(emptyPinArray(newLength));
      } else {
        setMessage({
          type: "error",
          text: formatApiError(result.data) || "No fue posible cambiar el PIN",
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Error de conexión" });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit =
    pinDigits(currentPin).length === currentLength &&
    pinDigits(newPin).length === newLength &&
    pinDigits(confirmPin).length === newLength;

  return (
    <div className={styles.container}>
      <div className={styles.securityIcon}>🔐</div>
      <p className={styles.description}>
        Tu PIN puede tener 4 o 6 dígitos; te recomendamos 6 porque es más
        seguro. Elige cuántos dígitos tiene tu PIN actual y cuántos tendrá el
        nuevo.
      </p>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.type === "success" ? "✓" : "⚠"} {message.text}
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.formGroup}>
          <label>PIN Actual ({currentLength} dígitos)</label>
          <PinLengthToggle
            value={currentLength}
            onChange={handleCurrentLengthChange}
            disabled={loading}
            label="¿Cuántos dígitos tiene tu PIN actual?"
            className={styles.lengthToggle}
          />
          <PinInput
            ref={currentPinRef}
            length={currentLength}
            value={currentPin}
            onChange={setCurrentPin}
            disabled={loading}
            ariaLabel="PIN actual"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Nuevo PIN ({newLength} dígitos)</label>
          <PinLengthToggle
            value={newLength}
            onChange={handleNewLengthChange}
            disabled={loading}
            label="Longitud del nuevo PIN"
            hint="6 dígitos es más seguro"
            className={styles.lengthToggle}
          />
          <PinInput
            ref={newPinRef}
            length={newLength}
            value={newPin}
            onChange={setNewPin}
            disabled={loading}
            ariaLabel="Nuevo PIN"
          />
        </div>

        <div className={styles.formGroup}>
          <label>Confirmar Nuevo PIN</label>
          <PinInput
            length={newLength}
            value={confirmPin}
            onChange={setConfirmPin}
            disabled={loading}
            ariaLabel="Confirmar nuevo PIN"
          />
        </div>

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={loading || !canSubmit}
        >
          {loading ? "Guardando..." : "Cambiar PIN"}
        </button>
      </form>
    </div>
  );
};

export default CambiarContrasena;
